import React, { useState } from 'react';
import { 
    MessageSquare, 
    Upload, 
    Send, 
    Mail, 
    Phone,
    HelpCircle,
    Image as ImageIcon,
    Loader2,
    X
} from 'lucide-react';
import { Button } from "@/components/UI/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/UI/card";
import { Textarea } from "@/components/UI/textarea";
import { useToast } from "@/components/UI/use-toast";

const Help = () => {
    const { toast } = useToast();
    const [message, setMessage] = useState('');
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);

    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        const validFiles = selectedFiles.filter(file => {
            const isValid = file.type.startsWith('image/');
            if (!isValid) {
                toast({
                    variant: "destructive",
                    title: "Invalid file type",
                    description: "Please upload only image files"
                });
            }
            return isValid;
        });
        setFiles(prev => [...prev, ...validFiles]);
    };

    const removeFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim() && files.length === 0) {
            toast({
                variant: "destructive",
                title: "Empty message",
                description: "Please enter a message or upload screenshots"
            });
            return;
        }

        setSending(true);
        try {
            // Here you would implement the actual email sending logic
            // For now, we'll simulate success
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            toast({
                title: "Message Sent Successfully",
                description: "We'll get back to you soon!",
                className: "bg-[#f3f1eb] border-[#e6e4e1]",
            });

            setMessage('');
            setFiles([]);
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Failed to send message",
                description: "Please try again or use alternative contact methods"
            });
        } finally {
            setSending(false);
        }
    };

    const handleWhatsApp = () => {
        window.open('https://wa.me/971524836526', '_blank');
    };

    const handleEmail = () => {
        window.location.href = 'mailto:avishkar@phoenician-uae.com';
    };

    return (
        <div className="min-h-screen bg-[#f8f7f4] p-6">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2 bg-gradient-to-r from-[#8b7355] to-[#6b563e] rounded-lg">
                        <HelpCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-[#6b563e]">Help & Support</h1>
                        <p className="text-[#8b7355]">Need assistance? We're here to help!</p>
                    </div>
                </div>

                {/* Contact Cards */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <Card className="bg-gradient-to-r from-[#f8f7f4] to-[#f3f1eb] border-[#e6e4e1]">
                        <CardContent className="p-6">
                            <div className="flex items-center space-x-4">
                                <div className="p-2 bg-[#f3f1eb] rounded-lg">
                                    <Phone className="h-5 w-5 text-[#8b7355]" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-[#6b563e]">WhatsApp Support</h3>
                                    <p className="text-sm text-[#8b7355] mb-4">Quick response via WhatsApp</p>
                                    <Button 
                                        onClick={handleWhatsApp}
                                        className="bg-[#25D366] hover:bg-[#128C7E] text-white w-full"
                                    >
                                        Chat on WhatsApp
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-r from-[#f8f7f4] to-[#f3f1eb] border-[#e6e4e1]">
                        <CardContent className="p-6">
                            <div className="flex items-center space-x-4">
                                <div className="p-2 bg-[#f3f1eb] rounded-lg">
                                    <Mail className="h-5 w-5 text-[#8b7355]" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-[#6b563e]">Email Support</h3>
                                    <p className="text-sm text-[#8b7355] mb-4">Send us a detailed email</p>
                                    <Button 
                                        onClick={handleEmail}
                                        className="bg-[#8b7355] hover:bg-[#6b563e] text-white w-full"
                                    >
                                        Send Email
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Message Form */}
                <Card className="bg-white border-[#e6e4e1]">
                    <CardHeader>
                        <CardTitle className="text-[#6b563e]">Send us a message</CardTitle>
                        <CardDescription className="text-[#8b7355]">
                            Describe your issue and attach screenshots if needed
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <Textarea
                                    placeholder="Type your message here..."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="min-h-[150px] border-[#e6e4e1] focus:border-[#8b7355] focus:ring-[#8b7355]"
                                />
                            </div>

                            {/* File Upload Section */}
                            <div className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <label className="cursor-pointer">
                                        <div className="flex items-center space-x-2 text-[#8b7355] hover:text-[#6b563e]">
                                            <Upload className="h-5 w-5" />
                                            <span>Upload Screenshots</span>
                                        </div>
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            multiple
                                            onChange={handleFileChange}
                                        />
                                    </label>
                                </div>

                                {files.length > 0 && (
                                    <div className="flex flex-wrap gap-4">
                                        {files.map((file, index) => (
                                            <div 
                                                key={index}
                                                className="relative group bg-[#f8f7f4] rounded-lg p-2 flex items-center space-x-2"
                                            >
                                                <ImageIcon className="h-4 w-4 text-[#8b7355]" />
                                                <span className="text-sm text-[#6b563e] max-w-[150px] truncate">
                                                    {file.name}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(index)}
                                                    className="p-1 hover:bg-[#e6e4e1] rounded-full"
                                                >
                                                    <X className="h-4 w-4 text-[#8b7355]" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Button
                                type="submit"
                                disabled={sending}
                                className="w-full bg-gradient-to-r from-[#8b7355] to-[#6b563e] 
                                         hover:from-[#6b563e] hover:to-[#574532] text-white"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send className="mr-2 h-4 w-4" />
                                        Send Message
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Help;